import React, { useEffect, useState } from 'react';
import { FaArrowLeft } from 'react-icons/fa6';
import { useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

import ModuloSubmodulos from './components/ModuloSubmodulos';
import BarraInferiorBotoes from '../../../../../../components/BarraInferiorBotoes';
import BarraSubAbas from '../../../../../../components/BarraSubAbas';
import BoxConteudo from '../../../../../../components/BoxConteudo';
import ContainerConteudo from '../../../../../../components/ContainerConteudo';
import CustomCol from '../../../../../../components/CustomCol';
import CustomInputText from '../../../../../../components/CustomInputs/CustomInputText';
import CustomRow from '../../../../../../components/CustomRow';
import CustomSelect from '../../../../../../components/CustomSelects/CustomSelect';
import StyledLink from '../../../../../../components/Link';
import Log from '../../../../../../components/Log/Log';
import SubAbas from '../../../../../../components/SubAbas';
import WorkspaceBotoes from '../../../../../../components/WorkspaceBotoes';
import WorkspaceFicha from '../../../../../../components/WorkspaceFicha';
import {
  IModuloForm,
  IModuloRegistro,
} from '../../../../../../modules/types/ModuloSisaf';
import { getModuloById } from '../../../../../../services/modulosSisaf/moduloService';
import { formataNomeMeioSiglas } from '../../../../../../utils/functions';
import { handleDelete } from '../../../../../../utils/handleDelete';
import { handleSave } from '../../../../../../utils/handleSave';
import { optionStatusBin } from '../../../Dominios/utils/optionStatusBin';
import SubAbasModulosSisaf from '../../utils/subabasModulos';

export default function ModuloIdentificacao() {
  const params = useParams();
  const fichaID = String(params.id);
  const theme = useTheme();

  const [logData, setLogData] = useState<IModuloRegistro | null>(null);
  const [originalForm, setOriginalForm] = useState<IModuloForm | null>(null);
  const [logRefresh, setLogRefresh] = useState(0);

  const [formMain, setFormMain] = useState<IModuloForm>({
    id: 0,
    status: 0,
    interfaceDaf: 'N',
    interfaceSes: 'N',
    interfaceFornecedor: 'N',
    modulo: '',
    descricao: '',
    observacoes: '',
  });

  const mapRegistroToForm = (registro: IModuloRegistro): IModuloForm => ({
    id: registro.id,
    status: registro.status,
    interfaceDaf: registro.interfaceDaf,
    interfaceSes: registro.interfaceSes,
    interfaceFornecedor: registro.interfaceFornecedor,
    modulo: registro.modulo,
    descricao: registro.descricao || '',
    observacoes: registro.observacoes || '',
  });

  const fetchData = async (id: string | number): Promise<void> => {
    try {
      const registroData: IModuloRegistro = await getModuloById(String(id));

      setLogData(registroData);

      const parsedForm = mapRegistroToForm(registroData);
      setFormMain(parsedForm);
      setOriginalForm(parsedForm);
      setLogRefresh((prev) => prev + 1);
    } catch (error) {
      console.error('Erro ao obter dados do módulo:', error);
    }
  };

  useEffect(() => {
    if (fichaID !== 'undefined') {
      fetchData(fichaID);
    }
  }, [fichaID]);

  const subAbas =
    fichaID !== 'undefined'
      ? SubAbasModulosSisaf(fichaID)
      : [
          {
            label: 'Dados Gerais',
            rota: `/daf/adm/modulossiaf/modulo/dadosgerais`,
          },
        ];

  const logs = logData
    ? {
        id: logData.id,
        usuarioRegistro: formataNomeMeioSiglas(
          logData.usuarioRegistro.nomeCompleto,
        ),
        usuarioAtualizacao: formataNomeMeioSiglas(
          logData.usuarioAtualizacao.nomeCompleto,
        ),
        dataRegistro: logData.dataRegistro,
        dataAtualizacao: logData.dataUltimaAtualizacao,
        nEdicoes: logData.nEdicoes,
      }
    : null;

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;
    setFormMain((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const camposObrigatorios = {
    status: 'Status do Módulo',
    modulo: 'Nome do Módulo',
  };

  const camposAjustar = {
    number: ['id', 'status'],
    string: [
      'modulo',
      'descricao',
      'observacoes',
      'interfaceDaf',
      'interfaceSes',
      'interfaceFornecedor',
    ],
  };

  const save = async () => {
    const saved = await handleSave(
      formMain,
      camposObrigatorios,
      camposAjustar,
      'modulos',
      'daf/adm/modulossiaf/modulo',
      undefined,
      true,
      originalForm || undefined,
    );

    if (saved && fichaID !== 'undefined') {
      await fetchData(fichaID);
    }
  };

  const handleDeleteWrapper = () =>
    handleDelete(fichaID, 'modulos', 'daf/adm/modulossiaf');

  return (
    <>
      <SubAbas>
        <StyledLink
          to="/daf/adm/modulossiaf"
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <FaArrowLeft />
        </StyledLink>
        <BarraSubAbas subAbas={subAbas} />
      </SubAbas>

      <ContainerConteudo>
        <WorkspaceFicha>
          {logs && (
            <Log {...logs} entity="TB_MODULO" refreshTriggerLog={logRefresh} />
          )}

          <BoxConteudo boxTitulo="Identificação do Módulo">
            <CustomRow
              style={{ marginTop: '-15px', justifyContent: 'flex-start' }}
            >
              <CustomCol width="250px">
                <CustomSelect
                  name="status"
                  options={optionStatusBin}
                  title="Status"
                  value={formMain.status}
                  onChange={handleChange}
                  style={{
                    backgroundColor:
                      Number(formMain.status) === 2
                        ? theme.colors.vermelho
                        : Number(formMain.status) === 1
                          ? theme.colors.verde
                          : 'white',
                    color:
                      Number(formMain.status) === 0 ? '#000000' : '#ffffff',
                  }}
                  required
                />
              </CustomCol>

              <CustomCol width="250px">
                <CustomSelect
                  name="interfaceDaf"
                  options={[
                    { value: 'S', label: 'Sim' },
                    { value: 'N', label: 'Não' },
                  ]}
                  title="Interface DAF"
                  value={formMain.interfaceDaf || 'N'}
                  onChange={handleChange}
                  required
                />
              </CustomCol>
              <CustomCol width="250px">
                <CustomSelect
                  name="interfaceSes"
                  options={[
                    { value: 'S', label: 'Sim' },
                    { value: 'N', label: 'Não' },
                  ]}
                  title="Interface SES"
                  value={formMain.interfaceSes || 'N'}
                  onChange={handleChange}
                  required
                />
              </CustomCol>
              <CustomCol width="250px">
                <CustomSelect
                  name="interfaceFornecedor"
                  options={[
                    { value: 'S', label: 'Sim' },
                    { value: 'N', label: 'Não' },
                  ]}
                  title="Interface Fornecedor"
                  value={formMain.interfaceFornecedor || 'N'}
                  onChange={handleChange}
                  required
                />
              </CustomCol>
              <CustomCol />
            </CustomRow>
            <CustomRow>
              <CustomCol>
                <CustomInputText
                  id="modulo"
                  name="modulo"
                  value={formMain.modulo}
                  title="Nome do Módulo"
                  onChange={handleChange}
                  required
                />
              </CustomCol>
            </CustomRow>

            <CustomRow>
              <CustomCol>
                <CustomInputText
                  id="descricao"
                  name="descricao"
                  value={formMain.descricao || ''}
                  title="Descrição"
                  type="textArea"
                  onChange={handleChange}
                  rows={4}
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol>
                <CustomInputText
                  id="observacoes"
                  name="observacoes"
                  value={formMain.observacoes || ''}
                  title="Observações"
                  type="textArea"
                  onChange={handleChange}
                  rows={3}
                />
              </CustomCol>
            </CustomRow>
          </BoxConteudo>

          {fichaID !== 'undefined' && (
            <ModuloSubmodulos
              moduloId={Number(fichaID)}
              onLogsRefresh={() => setLogRefresh((prev) => prev + 1)}
            />
          )}
        </WorkspaceFicha>
      </ContainerConteudo>

      <BarraInferiorBotoes>
        {fichaID !== 'undefined' ? (
          <WorkspaceBotoes
            delete
            onClickDelete={handleDeleteWrapper}
            save
            onClickSave={save}
          />
        ) : (
          <WorkspaceBotoes save onClickSave={save} />
        )}
      </BarraInferiorBotoes>
    </>
  );
}
